"use strict";

const {
  translateMarketDisplay,
  translateSelectionDisplay,
  translateLeagueDisplay,
} = require("./analysisDataMaps");

/**
 * Default locale for Arb presentation translation. Mirrors
 * `ANALYSIS_DEFAULT_LANG` from analysisTranslate.js, duplicated here (as a
 * plain string, not an import) to keep this module free of any dependency
 * on analysisTranslate.js and avoid a require cycle.
 */
const ARB_PRESENTATION_DEFAULT_LOCALE = "en-GB";

/**
 * Normalise the short-hand locale codes used across the Arb display path
 * into the full locale codes expected by the translation maps.
 *
 *   vi     -> vi-VN
 *   vi-VN  -> vi-VN
 *   en     -> en-GB
 *   en-GB  -> en-GB
 *   ""/nullish -> en-GB
 *   any other full locale -> returned unchanged
 */
function normalizeArbPresentationLocale(language) {
  const raw = String(language ?? "").trim();
  if (!raw) return ARB_PRESENTATION_DEFAULT_LOCALE;

  const lower = raw.toLowerCase();
  if (lower === "vi") return "vi-VN";
  if (lower === "en") return ARB_PRESENTATION_DEFAULT_LOCALE;

  return raw;
}

/* --------------------
ARB MARKET LABEL OVERRIDES (display-only)

These overrides apply only to the Arb leg/market rendering path. They cover
the small set of provider keys whose Vietnamese label needs to match the
in-product copy verbatim (e.g. "Cược chấp phạt góc" vs the more general
"Asian Handicap Corners" already in the i18n map).

Order of resolution:
  1) explicit override for `vi-VN` keyed by snake_case provider key
  2) fall back to the global `translateMarketDisplay`
-------------------- */

const ARB_MARKET_LABEL_OVERRIDES = {
  "vi-VN": {
    asian_handicap_corners: "Cược Chấp Góc Châu Á",
    corners_asian_handicap: "Cược Chấp Góc Châu Á",
    asian_corners_handicap: "Cược Chấp Góc Châu Á",
    corner_handicap: "Cược Chấp Phạt Góc",
    total_corners: "Tổng Phạt Góc",
    "1st_half_total_corners": "Tài/Xỉu Phạt Góc · HT",
    first_half_total_corners: "Tài/Xỉu Phạt Góc · HT",
    "2nd_half_total_corners": "Tài/Xỉu Phạt Góc · 2H",
    second_half_total_corners: "Tài/Xỉu Phạt Góc · 2H",
    // Team total family — base label only; the Arb header builder
    // appends the period scope (HT / 2H / FT / Q1..Q4) after it. Each
    // leg row carries its own unsigned line.
    team_total: "Tổng Đội",
    team_totals: "Tổng Đội",
    team_total_goals: "Tổng Bàn Thắng Đội",
    team_total_points: "Tổng Điểm Đội",
    team_total_runs: "Tổng Runs Đội",
    team_total_corners: "Tổng Phạt Góc Đội",
    "1st_half_team_total": "Tổng Đội · HT",
    first_half_team_total: "Tổng Đội · HT",
    "2nd_half_team_total": "Tổng Đội · 2H",
    second_half_team_total: "Tổng Đội · 2H",
    "1st_half_team_total_goals": "Tổng Bàn Thắng Đội · HT",
    first_half_team_total_goals: "Tổng Bàn Thắng Đội · HT",
    "2nd_half_team_total_goals": "Tổng Bàn Thắng Đội · 2H",
    second_half_team_total_goals: "Tổng Bàn Thắng Đội · 2H",
    "1st_half_team_total_points": "Tổng Điểm Đội · HT",
    first_half_team_total_points: "Tổng Điểm Đội · HT",
    "2nd_half_team_total_points": "Tổng Điểm Đội · 2H",
    second_half_team_total_points: "Tổng Điểm Đội · 2H",
    "1st_half_team_total_runs": "Tổng Runs Đội · HT",
    first_half_team_total_runs: "Tổng Runs Đội · HT",
    "2nd_half_team_total_runs": "Tổng Runs Đội · 2H",
    second_half_team_total_runs: "Tổng Runs Đội · 2H",
    "1st_half_team_total_corners": "Tổng Phạt Góc Đội · HT",
    first_half_team_total_corners: "Tổng Phạt Góc Đội · HT",
    "2nd_half_team_total_corners": "Tổng Phạt Góc Đội · 2H",
    second_half_team_total_corners: "Tổng Phạt Góc Đội · 2H",
    moneyline: "Kèo Thắng",
    match_winner: "Kèo Thắng",
    "1x2": "1X2",
    three_way_moneyline: "1X2",
    moneyline_3_way: "1X2",
    point_spread: "Chấp",
    spread: "Chấp",
    handicap: "Chấp",
    run_line: "Chấp",
    puck_line: "Chấp",
    set_handicap: "Chấp Set",
    asian_handicap: "Chấp Châu Á",
    total: "Tài/Xỉu",
    total_points: "Tài/Xỉu Tổng",
    total_goals: "Tài/Xỉu Tổng",
    total_runs: "Tài/Xỉu Tổng",
    total_games: "Tài/Xỉu Tổng",
  },
};

function _arbNormaliseProviderKey(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * For team_total markets coming from legacy `item.market` strings (which
 * may include a team name, e.g. "1st Half - Sporting Braga - Total Goals"),
 * the snake-case key won't equal one of the explicit override entries.
 * Detect the family by substring and map back to the canonical override
 * label so the Arb header reads e.g. "Tổng bàn thắng đội · HT" regardless
 * of whether the leg-shape provided market_type or only the legacy market.
 *
 * Returns "" when no team_total family is detected.
 */
function _arbResolveTeamTotalSubstringOverride(key, langMap) {
  if (!key || !langMap) return "";
  if (!key.includes("team_total")) return "";

  const has = (re) => re.test(key);

  const corners = has(/\bcorners?\b/);
  const points = has(/\bpoints?\b/);
  const runs = has(/\bruns?\b/);
  const goals = has(/\bgoals?\b/);

  if (corners && Object.prototype.hasOwnProperty.call(langMap, "team_total_corners")) {
    return langMap.team_total_corners;
  }
  if (points && Object.prototype.hasOwnProperty.call(langMap, "team_total_points")) {
    return langMap.team_total_points;
  }
  if (runs && Object.prototype.hasOwnProperty.call(langMap, "team_total_runs")) {
    return langMap.team_total_runs;
  }
  if (goals && Object.prototype.hasOwnProperty.call(langMap, "team_total_goals")) {
    return langMap.team_total_goals;
  }
  // Plain team_total without a more specific scoring sub-family.
  if (Object.prototype.hasOwnProperty.call(langMap, "team_total")) {
    return langMap.team_total;
  }
  return "";
}

/**
 * Translate a market identifier for the Arb display path. Prefers the
 * Arb-specific override map for the requested language, then falls back to
 * the global `translateMarketDisplay`. Pure / display-only.
 */
function translateArbMarketLabel(rawMarket, lang) {
  const normalizedLang = normalizeArbPresentationLocale(lang);
  const key = _arbNormaliseProviderKey(rawMarket);
  if (key) {
    const langMap = ARB_MARKET_LABEL_OVERRIDES[normalizedLang];
    if (langMap) {
      if (Object.prototype.hasOwnProperty.call(langMap, key)) {
        return langMap[key];
      }
      // Fallback for legacy `item.market` strings that embed team names
      // alongside a team_total family token. We never invent a team here —
      // the team prefix surfaces in each leg row, not in the header.
      const teamTotalOverride = _arbResolveTeamTotalSubstringOverride(key, langMap);
      if (teamTotalOverride) return teamTotalOverride;
    }
  }
  return translateMarketDisplay(String(rawMarket || "").trim(), normalizedLang) || "";
}

/**
 * Translate a league name for the Arb display path. Normalises the locale
 * and delegates entirely to the shared `translateLeagueDisplay`. Pure /
 * display-only.
 */
function translateArbLeagueLabel(rawLeague, lang) {
  const normalizedLang = normalizeArbPresentationLocale(lang);
  return translateLeagueDisplay(rawLeague, normalizedLang);
}

/**
 * Translator bag passed to the sharpArbAdapter `buildArbMarketDisplayLabel`
 * and `buildArbLegDisplayLabel` helpers. The adapter remains free of i18n
 * dependencies; we inject the translators here.
 */
function getArbDisplayTranslatorBag(lang) {
  const normalizedLang = normalizeArbPresentationLocale(lang);
  return {
    lang: normalizedLang,
    translateMarket: (raw, lng) => translateArbMarketLabel(raw, lng || normalizedLang),
    translateSelection: (raw, lng) =>
      translateSelectionDisplay(
        raw,
        normalizeArbPresentationLocale(lng || normalizedLang)
      ),
  };
}

module.exports = {
  normalizeArbPresentationLocale,
  translateArbMarketLabel,
  translateArbLeagueLabel,
  getArbDisplayTranslatorBag,
};
